import { useState, useEffect } from "react";
import { Loader2, Plus, ShieldCheck, Trash2, KeyRound, Pencil, Crown } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { owner: "Propietario", admin: "Admin" };

const Users = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", whatsapp: "" });
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [editTarget, setEditTarget] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/users").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.password) return toast.error("Completá email y contraseña");
    try {
      await api.post("/admin/users", form);
      toast.success("Administrador creado");
      setOpenNew(false); setForm({ name: "", email: "", password: "", whatsapp: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/users/${editTarget.id}`, {
        name: editTarget.name, email: editTarget.email, whatsapp: editTarget.whatsapp, role: editTarget.role,
      });
      toast.success("Administrador actualizado");
      setEditTarget(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Mínimo 6 caracteres");
    try {
      await api.patch(`/admin/users/${pwTarget.id}/password`, { password: newPw });
      toast.success("Contraseña actualizada");
      setPwTarget(null); setNewPw("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar al administrador ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Administrador eliminado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Administradores" subtitle="Gestioná los accesos al panel. Cada admin tiene su propio email y contraseña."
        action={<Button onClick={() => setOpenNew(true)} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="new-admin-btn"><Plus className="h-4 w-4 mr-1" /> Nuevo admin</Button>} />

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : items.length === 0 ? <EmptyState icon={ShieldCheck} text="Sin administradores." />
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((u) => (
              <div key={u.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`admin-${u.email}`}>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-indigo-500 to-fuchsia-600 flex items-center justify-center text-white font-bold">{(u.name || u.email).charAt(0).toUpperCase()}</div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5">{u.name || "Admin"} {u.id === user?.id && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Vos</span>}</p>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${u.role === "owner" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`} data-testid={`admin-role-${u.email}`}>
                    {u.role === "owner" && <Crown className="h-3 w-3" />} {ROLE_LABEL[u.role] || "Admin"}
                  </span>
                  {u.whatsapp && <span className="text-xs text-slate-400">· {u.whatsapp}</span>}
                </div>
                <p className="text-xs text-slate-400 mt-2">Creado: {formatDate(u.created_at)}</p>
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-lg flex-1" onClick={() => setEditTarget({ id: u.id, name: u.name || "", email: u.email, whatsapp: u.whatsapp || "", role: u.role || "admin" })} data-testid={`edit-admin-${u.email}`}><Pencil className="h-4 w-4 mr-1" /> Editar</Button>
                  <Button variant="outline" size="sm" className="rounded-lg flex-1" onClick={() => { setPwTarget(u); setNewPw(""); }} data-testid={`reset-pw-${u.email}`}><KeyRound className="h-4 w-4 mr-1" /> Clave</Button>
                  {u.id !== user?.id && (
                    <Button variant="outline" size="sm" className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => del(u)} data-testid={`del-admin-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo administrador</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1" placeholder="Nombre" data-testid="admin-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg mt-1" placeholder="admin@email.com" data-testid="admin-email-input" /></div>
            <div><Label>WhatsApp <span className="text-slate-400 text-xs">(opcional)</span></Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="rounded-lg mt-1" placeholder="+595 9XX XXXXXX" data-testid="admin-whatsapp-input" /></div>
            <div><Label>Contraseña</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-lg mt-1" placeholder="Mínimo 6 caracteres" data-testid="admin-password-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={create} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-admin">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar administrador</DialogTitle></DialogHeader>
          {editTarget && (
            <div className="space-y-4">
              <div><Label>Nombre</Label><Input value={editTarget.name} onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })} className="rounded-lg mt-1" data-testid="edit-admin-name" /></div>
              <div><Label>Email</Label><Input type="email" value={editTarget.email} onChange={(e) => setEditTarget({ ...editTarget, email: e.target.value })} className="rounded-lg mt-1" data-testid="edit-admin-email" /></div>
              <div><Label>WhatsApp</Label><Input value={editTarget.whatsapp} onChange={(e) => setEditTarget({ ...editTarget, whatsapp: e.target.value })} className="rounded-lg mt-1" placeholder="+595 9XX XXXXXX" data-testid="edit-admin-whatsapp" /></div>
              <div>
                <Label>Función</Label>
                <Select value={editTarget.role} onValueChange={(v) => setEditTarget({ ...editTarget, role: v })}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="edit-admin-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Propietario (acceso total)</SelectItem>
                    <SelectItem value="admin">Admin (acceso limitado)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1.5">El Propietario puede editar servicios, precios, configuración, proveedores y administradores. El Admin gestiona pedidos, comprobantes y tickets.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} className="rounded-lg">Cancelar</Button>
            <Button onClick={saveEdit} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-edit-admin">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Cambiar contraseña</DialogTitle></DialogHeader>
          {pwTarget && <p className="text-sm text-slate-500 -mt-2">{pwTarget.email}</p>}
          <div><Label>Nueva contraseña</Label><Input value={newPw} onChange={(e) => setNewPw(e.target.value)} className="rounded-lg mt-1" placeholder="Mínimo 6 caracteres" data-testid="new-password-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwTarget(null)} className="rounded-lg">Cancelar</Button>
            <Button onClick={resetPw} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-password">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
